/**
 * ProfileImage.js
 * Displays user profile images with fallback to initials avatar
 */

import React, { useState } from 'react';

/**
 * ProfileImage Component
 * @param {Object} props
 * @param {Object} props.user - User object with name, profile_image_url, etc.
 * @param {number} props.size - Size in pixels (default: 40)
 * @param {string} props.borderColor - Optional border color
 * @param {Function} props.onClick - Optional click handler
 * @param {Object} props.style - Additional inline styles
 */
export function ProfileImage({ user, size = 40, borderColor, onClick, style = {} }) {
  const [imageError, setImageError] = useState(false);

  // Generate initials from name (letters only, max 2)
  const getInitials = (name) => {
    if (!name) return '?';
    const letters = name.replace(/[^a-zA-Z]/g, '');
    if (!letters) return '?';
    const parts = name.trim().split(/\s+/).filter(p => /[a-zA-Z]/.test(p));
    if (parts.length >= 2) {
      const first = parts[0].match(/[a-zA-Z]/)?.[0] || '';
      const last = parts[parts.length - 1].match(/[a-zA-Z]/)?.[0] || '';
      return (first + last).toUpperCase().slice(0, 2);
    }
    return letters.substring(0, 2).toUpperCase();
  };

  // Generate consistent color from name
  const getColorFromName = (name) => {
    if (!name) return '#808080';
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 60%, 50%)`;
  };

  const hasImage = user?.profile_image_url && !imageError && !user.profile_image_url.startsWith('data:image/svg');

  const commonStyle = {
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: '50%',
    objectFit: 'cover',
    border: borderColor ? `2px solid ${borderColor}` : '2px solid #ddd',
    cursor: onClick ? 'pointer' : 'default',
    flexShrink: 0,
    ...style
  };

  if (hasImage) {
    return (
      <img
        src={user.profile_image_url}
        alt={user.name || 'User'}
        style={commonStyle}
        onError={() => setImageError(true)}
        onClick={onClick}
        title={user.name}
      />
    );
  }

  // Fallback to initials avatar
  const baseColor = borderColor || getColorFromName(user?.name);
  return (
    <div
      style={{
        ...commonStyle,
        background: `linear-gradient(135deg, ${baseColor}, ${baseColor}99)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontWeight: 'bold',
        fontSize: `${size * 0.4}px`,
        userSelect: 'none',
        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
      }}
      onClick={onClick}
      title={user?.name || 'User'}
    >
      {getInitials(user?.name || 'User')}
    </div>
  );
}

/**
 * ProfileImageList Component
 * Displays a row of profile images (useful for showing multiple users)
 */
export function ProfileImageList({ users, size = 32, maxDisplay = 5, onClick }) {
  const displayUsers = users.slice(0, maxDisplay);
  const remaining = users.length - maxDisplay;

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '4px'
    }}>
      {displayUsers.map((user, index) => (
        <ProfileImage
          key={user.id || index}
          user={user}
          size={size}
          onClick={onClick ? () => onClick(user) : undefined}
          style={{ marginLeft: index > 0 ? '-8px' : '0' }}
        />
      ))}
      {remaining > 0 && (
        <div style={{
          width: `${size}px`,
          height: `${size}px`,
          borderRadius: '50%',
          backgroundColor: '#e0e0e0', textShadow: 'none',
          border: '2px solid #ddd',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: `${size * 0.35}px`,
          fontWeight: 'bold',
          color: '#666',
          marginLeft: '-8px'
        }}>
          +{remaining}
        </div>
      )}
    </div>
  );
}

export default ProfileImage;
